import { HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AppConstants } from "../app.constants";
import { retry, timeout } from "rxjs/operators";
import { environment } from "../../environments/environment";

@Injectable()
export class AuthenticatedHttp {

  constructor(private http: HttpClient) {
  }

  /**
   * Make a GET request to the target URL
   * @param url: GET request URL
   * @param {any} params: Query string params
   * @param {any} data: Body of the request
   * @returns {Observable<T>}: emits the response when received
   */
  get<T>(url, params: HttpParams = null, data = null): Observable<T> {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    if (environment.production) {
      return this.http.request<T>("get", url, {
        body: data,
        headers: {Authorization: "Bearer " + token},
        withCredentials: true,
        params
      }).pipe(
        timeout(AppConstants.HTTP_REQUEST_TIMEOUT),
        retry(AppConstants.HTTP_REQUEST_RETRIES)
      );
    } else {
      return this.http.request<T>("get", url, {
        body: data,
        headers: {Authorization: "Bearer " + token},
        withCredentials: true,
        params
      });
    }
  }

  /**
   * Make a generic request to the target URL
   * @param url: Request URL
   * @param data: Body of the request
   * @param {HttpParams} params: Serialized parameters
   * @param {string} method: Request method
   * @param {string} contentType: Mimetype of the request
   * @param {boolean} progress: Whether HTTP events (including upload/download progress events) should be emitted
   * @returns {Observable<any>}
   */
  post<T>(url, data, params: HttpParams = null, method: string = "post", contentType: string = "application/json", progress: boolean = false, retryRequest: boolean = true): Observable<any> {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    const headers = new HttpHeaders({Authorization: "Bearer " + token});
    if (contentType !== "multipart/form-data") {
      headers.append("Content-Type", contentType);
    }

    const observer: any = progress ? "events" : "body";
    if (environment.production) {
      if (retryRequest) {
        return this.http.request<T>(method, url, {
          body: data,
          headers,
          withCredentials: true,
          params,
          observe: observer,
          reportProgress: progress
        }).pipe(
          timeout(AppConstants.HTTP_REQUEST_TIMEOUT),
          retry(AppConstants.HTTP_REQUEST_RETRIES)
        );
      } else {
        return this.http.request<T>(method, url, {
          body: data,
          headers,
          withCredentials: true,
          params,
          observe: observer,
          reportProgress: progress
        }).pipe(
          timeout(AppConstants.HTTP_REQUEST_TIMEOUT)
        );
      }
    } else {
      console.log({
        body: data,
        headers,
        withCredentials: true,
        params,
        observe: observer,
        reportProgress: progress
      });
      return this.http.request<T>(method, url, {
        body: data,
        headers,
        withCredentials: true,
        params,
        observe: observer,
        reportProgress: progress
      });
    }
  }

  /**
   * Make a PATCH request to the target URL
   * @param url: DELETE request URL
   * @param data: Body of the request
   * @param {HttpParams} params: Serialized parameters
   * @returns {Observable<T>}
   */
  patch<T>(url, data, params: HttpParams = null): Observable<T> {
    return this.post(url, data, params, "patch");
  }

  /**
   * Make a PUT request to the target URL
   * @param url: DELETE request URL
   * @param data: Body of the request
   * @param {HttpParams} params: Serialized parameters
   * @returns {Observable<T>}
   */
  put<T>(url, data, params: HttpParams = null): Observable<T> {
    return this.post(url, data, params, "put");
  }

  /**
   * Make a DELETE request to the target URL
   * @param url: DELETE request URL
   * @param data: Body of the request
   * @param {HttpParams} params: Serialized parameters
   * @returns {Observable<T>}
   */
  delete<T>(url, data, params: HttpParams = null): Observable<T> {
    return this.post(url, data, params, "delete");
  }

  /**
   * GET an image from the target URL
   * @param url
   * @returns {Observable<Object>}
   */
  getImage(url) {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    return this.http.get(url, {
      headers: {"Authorization": "Bearer " + token, "Content-Type": "image/png"},
      responseType: "blob" as "json",
      withCredentials: true
    });
  }

  /**
   * GET a video from the target URL
   * @param url
   * @returns {Observable<Object>}
   */
  getVideo(url) {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    return this.http.get(url, {
      headers: {"Authorization": "Bearer " + token, "Content-Type": "video/mp4"},
      responseType: "blob" as "json",
      withCredentials: true
    });
  }

  /**
   * GET a file from the target URL
   * @param url
   * @returns {Observable<HttpResponse<Blob>>}
   */
  getFile(url) {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    return this.http.get(url, {
      headers: {Authorization: "Bearer " + token},
      observe: "response",
      responseType: "blob" as "json",
      withCredentials: true
    });
  }

  /**
   * Get files via POST from the target URL
   * @param url
   * @param method
   * @param data
   * @returns {Observable<HttpResponse<Blob>>}
   */
  getFiles(url, method, data: any = null) {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    const token = JSON.parse(authObject);
    if (method === "get") {
      return this.http.get(url, {
        headers: {Authorization: "Bearer " + token},
        observe: "response",
        responseType: "blob" as "json",
        withCredentials: true
      });
    } else if (method === "post") {
      return this.http.post(url, data, {
        headers: {Authorization: "Bearer " + token},
        observe: "response",
        responseType: "blob" as "json",
        withCredentials: true
      });
    } else {
      return null;
    }
  }

  jsonp(url) {
    return this.http.jsonp(url, "JSONP_CALLBACK")
      .pipe(
        retry(environment.production ? 2 : 0)
      );
  }
}
